import { Button, Space, Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import type { Tab } from 'rc-tabs/lib/interface';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import DisbursalTypeEnum from '~Api/Application/DisbursalTypeEnum';
import ILoan from '~Api/Loan/ILoan';
import ILoanTransaction from '~Api/Loan/ILoanTransaction';
import {
    loanGetAction,
    loanTransactionsListAction,
} from '~Loans/actions';
import {
    loanSelector,
    loanTransactionsSelector,
} from '~Loans/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';
import { currencyFormatter } from '~utilities/formatters';
import HashTabs from '~UI/HashTabs';

interface ITransaction {
    amount: number;
    balance: number;
    description: string;
    transactionTime: string;
    uuid: string;
}

export default function Transactions(): ReactElement {
    const { uuid } = useParams<{ uuid: string }>();

    const loan: ILoan = useSelector((state: IGlobalState): ILoan => loanSelector(state, uuid));
    const transactions: IDictionary<ILoanTransaction> = useSelector((state: IGlobalState): IDictionary<ILoanTransaction> => loanTransactionsSelector(state, uuid));
    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!loan) {
            dispatch(loanGetAction(uuid));
        }
    }, [
        dispatch,
        loan,
        uuid,
    ]);

    useEffect((): void => {
        if (!transactions) {
            dispatch(loanTransactionsListAction(uuid));
        }
    }, [
        dispatch,
        transactions,
        uuid,
    ]);

    if (!loan || !transactions) {
        return (
            <Layout uuid={uuid} section='transactions'>
                <Typography.Title level={2}>Transactions</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const statementColumns: ColumnsType<ITransaction> = [
        {
            dataIndex: 'transactionTime',
            render: (transactionTime: string): ReactNode => dayjs(transactionTime).format('D/M/YYYY'),
            title: 'Date',
            width: '10%',
        },
        {
            dataIndex: 'description',
            title: 'Description',
        },
        {
            align: 'right',
            dataIndex: 'amount',
            render: (amount: number): ReactNode => amount < 0 ? currencyFormatter.format(amount) : null,
            title: 'Debit',
            width: '15%',
        },
        {
            align: 'right',
            dataIndex: 'amount',
            render: (amount: number): ReactNode => amount > 0 ? currencyFormatter.format(amount) : null,
            title: 'Credit',
            width: '15%',
        },
        {
            align: 'right',
            dataIndex: 'balance',
            render: (balance: number): ReactNode => currencyFormatter.format(balance),
            title: 'Balance',
            width: '15%',
        },
    ];

    let interestBalance: number = 0;
    let defaultInterestBalance: number = 0;
    let prepaidInterestBalance: number = 0;
    let principalBalance: number = 0;
    let retainedContingencyBalance: number = 0;
    let retainedFundsBalance: number = 0;
    let otherRetainedFundsBalance: number = 0;
    const interestTransactions: ITransaction[] = [];
    const defaultInterestTransactions: ITransaction[] = [];
    const prepaidInterestTransactions: ITransaction[] = [];
    const principalTransactions: ITransaction[] = [];
    const cleanedTransactions: ITransaction[] = [];
    const retainedContingencyTransactions: ITransaction[] = [];
    const retainedFundsTransactions: ITransaction[] = [];
    const otherRetainedFundsTransactions: ITransaction[] = [];

    let balance: number = 0;

    _.each(_.sortBy(transactions, ['transactionTime']), (loanTransaction: ILoanTransaction): void => {
        if (
            loanTransaction.amountInterest !== 0
            || loanTransaction.amountPrincipal !== 0
            || (
                loanTransaction.amountDefaultInterest !== null
                && loanTransaction.amountDefaultInterest !== 0
            )
        ) {
            balance += loanTransaction.amountInterest + loanTransaction.amountPrincipal + (loanTransaction.amountDefaultInterest || 0);

            cleanedTransactions.push({
                amount: loanTransaction.amountInterest + loanTransaction.amountPrincipal + (loanTransaction.amountDefaultInterest || 0),
                balance,
                description: loanTransaction.description,
                transactionTime: loanTransaction.transactionTime,
                uuid: loanTransaction.uuid,
            });
        }

        if (loanTransaction.amountInterest !== 0) {
            interestBalance += loanTransaction.amountInterest;

            interestTransactions.push({
                amount: loanTransaction.amountInterest,
                balance: interestBalance,
                description: loanTransaction.description,
                transactionTime: loanTransaction.transactionTime,
                uuid: loanTransaction.uuid,
            });
        }

        if (
            loanTransaction.amountDefaultInterest !== null
            && loanTransaction.amountDefaultInterest !== 0
        ) {
            defaultInterestBalance += loanTransaction.amountDefaultInterest;

            defaultInterestTransactions.push({
                amount: loanTransaction.amountDefaultInterest,
                balance: defaultInterestBalance,
                description: loanTransaction.description,
                transactionTime: loanTransaction.transactionTime,
                uuid: loanTransaction.uuid,
            });
        }

        if (loanTransaction.amountPrepaidInterest !== 0) {
            prepaidInterestBalance += loanTransaction.amountPrepaidInterest;

            prepaidInterestTransactions.push({
                amount: loanTransaction.amountPrepaidInterest,
                balance: prepaidInterestBalance,
                description: loanTransaction.description,
                transactionTime: loanTransaction.transactionTime,
                uuid: loanTransaction.uuid,
            });
        }

        if (loanTransaction.amountPrincipal !== 0) {
            principalBalance += loanTransaction.amountPrincipal;

            principalTransactions.push({
                amount: loanTransaction.amountPrincipal,
                balance: principalBalance,
                description: loanTransaction.description,
                transactionTime: loanTransaction.transactionTime,
                uuid: loanTransaction.uuid,
            });
        }

        if (loanTransaction.retainedFunds !== 0) {
            retainedFundsBalance += loanTransaction.retainedFunds;

            retainedFundsTransactions.push({
                amount: loanTransaction.retainedFunds,
                balance: retainedFundsBalance,
                description: loanTransaction.description,
                transactionTime: loanTransaction.transactionTime,
                uuid: loanTransaction.uuid,
            });
        }

        if (loanTransaction.retainedContingency !== 0) {
            retainedContingencyBalance += loanTransaction.retainedContingency;

            retainedContingencyTransactions.push({
                amount: loanTransaction.retainedContingency,
                balance: retainedContingencyBalance,
                description: loanTransaction.description,
                transactionTime: loanTransaction.transactionTime,
                uuid: loanTransaction.uuid,
            });
        }

        if (loanTransaction.otherRetainedFunds !== 0) {
            otherRetainedFundsBalance += loanTransaction.otherRetainedFunds;

            otherRetainedFundsTransactions.push({
                amount: loanTransaction.otherRetainedFunds,
                balance: otherRetainedFundsBalance,
                description: loanTransaction.description,
                transactionTime: loanTransaction.transactionTime,
                uuid: loanTransaction.uuid,
            });
        }
    });

    const columns: ColumnsType<ITransaction> = [
        {
            dataIndex: 'transactionTime',
            defaultSortOrder: 'descend',
            render: (transactionTime: string): ReactNode => dayjs(transactionTime).format('D/M/YYYY HH:mm'),
            sorter: (a: ITransaction, b: ITransaction): number => {
                return a.transactionTime > b.transactionTime ? 1 : -1;
            },
            title: 'Date',
            width: '15%',
        },
        {
            dataIndex: 'description',
            title: 'Description',
        },
        {
            dataIndex: 'amount',
            render: (amount: number): ReactNode => currencyFormatter.format(amount),
            title: 'Amount',
            width: '15%',
        },
        {
            dataIndex: 'balance',
            render: (balance: number): ReactNode => currencyFormatter.format(balance),
            title: 'Balance',
            width: '15%',
        },
    ];

    const statementButton: JSX.Element = (
        loan.paymentDateNext
        && dayjs(loan.paymentDateNext) > dayjs()
        && loan.amount === loan.amountRemaining
        && dayjs(loan.startDate) >= dayjs('2023-07-01')
    ) && (
        <Button
            href={`${process.env.API_HOST}/loans/${loan.uuid}/statement`}
            target='_blank'
        >
            Download Statement
        </Button>
    );

    const tabItems: Tab[] = [
        {
            children: (
                <Table
                    columns={statementColumns}
                    dataSource={cleanedTransactions}
                    rowKey='uuid'
                    pagination={false}
                    size='middle'
                />
            ),
            key: 'statement',
            label: 'Statement',
        },
        {
            children: (
                <Table
                    columns={columns}
                    dataSource={principalTransactions}
                    pagination={false}
                    rowKey='uuid'
                    size='middle'
                />
            ),
            key: 'principal',
            label: 'Principal',
        },
        {
            children: (
                <Table
                    columns={columns}
                    dataSource={interestTransactions}
                    pagination={false}
                    rowKey='uuid'
                    size='middle'
                />
            ),
            key: 'interest',
            label: 'Interest',
        },
        {
            children: (
                <Table
                    columns={columns}
                    dataSource={prepaidInterestTransactions}
                    pagination={false}
                    rowKey='uuid'
                    size='middle'
                />
            ),
            key: 'prepaid-interest',
            label: 'Prepaid Interest',
        },
    ];

    if (loan.application.disbursalType === DisbursalTypeEnum.Drawdown) {
        tabItems.push(
            {
                children: (
                    <Table
                        columns={columns}
                        dataSource={retainedFundsTransactions}
                        pagination={false}
                        rowKey='uuid'
                        size='middle'
                    />
                ),
                key: 'retained-funds',
                label: 'Retained Funds',
            },
            {
                children: (
                    <Table
                        columns={columns}
                        dataSource={retainedContingencyTransactions}
                        pagination={false}
                        rowKey='uuid'
                        size='middle'
                    />
                ),
                key: 'retained-contingency',
                label: 'Retained Contingency',
            },
        );
    }

    if (otherRetainedFundsTransactions.length > 0) {
        tabItems.push({
            children: (
                <Table
                    columns={columns}
                    dataSource={otherRetainedFundsTransactions}
                    pagination={false}
                    rowKey='uuid'
                    size='middle'
                />
            ),
            key: 'other-retained-funds',
            label: 'Other Retained Funds',
        });
    }

    tabItems.push({
        children: (
            <Table
                columns={columns}
                dataSource={defaultInterestTransactions}
                pagination={false}
                rowKey='uuid'
                size='middle'
            />
        ),
        key: 'default-interest',
        label: 'Default Interest',
    });

    return (
        <Layout uuid={uuid} section='transactions'>
            <Space className='actions'>
                {statementButton}
            </Space>
            <Typography.Title level={2}>Transactions</Typography.Title>
            <HashTabs
                defaultActiveKey='statement'
                items={tabItems}
            />
        </Layout>
    );
}
